import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { UTILS } from "utils";
import { separateMediaUrlsByType } from "utils/Utils";
import { getCurrentUserTempUrl } from "hooks/useCurrentUserItems";
import UnsavedChangesWarning from "components/UnsavedChangesWarning";
import { API } from "api";
import AppContainer from "components/ui/AppContainer";
import ItemsForm from "./ItemsForm";
import { errorHandler } from "api/api/errorHandler";
import useGroupModifierApi from "features/Modifier/useGroupModifierApi";
import AttachItemToModifier from "./AttachItemToModifier";

export const transformPayload = (currentItem) => {
  const userDetail = UTILS.getCurrentUser();
  const payload = {
    itemId: currentItem?._id,
    baseLanguage: {
      ...currentItem.baseLanguage,
      description: currentItem?.baseLanguage?.description?.trim() ?? null,
    },
    maximumOrderQuantity: currentItem?.maximumOrderQuantity,
    minimumOrderQuantity: currentItem?.minimumOrderQuantity,
    isItemPriceGiven: currentItem?.isItemPriceGiven,
    sku: currentItem?.sku,
    isStockEnabled: currentItem?.isStockEnabled,
    availableStock: currentItem?.availableStock,
    lowStockThreshold: currentItem?.lowStockThreshold,
    itemImageURLs: [
      ...(currentItem?.itemImageURLs ?? []),
      ...(currentItem?.videos ?? []),
    ]?.map((url) => url?.url),
    price: +parseFloat(currentItem?.price)?.toFixed(2) ?? 0,
    brandId: userDetail.brandId,
  };
  return payload;
};
const UpdateItem = ({ inline = false, itemId: itemIdProp = null, onUpdated = null, onCancel = null }) => {
  // hooks
  const { t } = useTranslation();
  const router = useHistory();
  const [itemId, setItemId] = useState(null);
  const { integrations } = useSelector((state) => state.SettingsReducer);
  const isAiInfluencerEnabled = useMemo(() => {
    return (
      integrations?.some(
        (integration) => integration?.name === "CREATIFY_AI"
      ) || false
    );
  }, [integrations]);
  // states
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    item: null,
  });
  const [originalItem, setOriginalItem] = useState(UTILS.getItemModel());
  const [item, setItem] = useState(UTILS.getItemModel());
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [toBeAttachedCustomizations, setToBeAttachedCustomizations] = useState(
    []
  );
  const [toBeUnLinkCustomizations, setToBeUnLinkCustomizations] = useState([]);
  const [showAiInfluencerButton, setShowAiInfluencerButton] = useState(false);
  const { linkModifierGroupsWithItems, UnlinkModiferGroupFromItem } =
    useGroupModifierApi();

  const getItemDetail = async (id) => {
    setLoading(true);
    setItemId(id);
    const userDetail = UTILS.getCurrentUser();
    const tempUrls = await getCurrentUserTempUrl({ itemId: id });
    if (tempUrls?.length > 0) {
      setIsDirty(true);
    }
    const result = await API.GET_ITEM_INFO({
      itemId: id,
      token: userDetail.token,
      brandId: userDetail.brandId,
    });
    if (result?.success === 1) {
      // check if items has valid media urls, then show ai influencer button
      if (result?.data?.itemImageURLs?.length > 0)
        setShowAiInfluencerButton(true);
      let uniqueUrls = new Set([
        ...(tempUrls ?? []),
        ...result.data.itemImageURLs,
      ]);
      uniqueUrls = Array.from(uniqueUrls);
      const sortedUrls = separateMediaUrlsByType(uniqueUrls, tempUrls);
      setOriginalItem((prevState) => ({
        values: {
          ...result.data,
          videos: sortedUrls.videoUrls,
          itemImageURLs: sortedUrls.imgUrls,
        },
        errors: {
          ...prevState.errors,
        },
      }));
      setItem((prevState) => ({
        values: {
          ...result.data,
          videos: sortedUrls.videoUrls,
          itemImageURLs: sortedUrls.imgUrls,
        },
        errors: {
          ...prevState.errors,
        },
      }));
    } else {
    }
    setLoading(false);
  };

  useEffect(() => {
    const resolvedItemId = itemIdProp || router?.location?.state?.itemId;
    if (resolvedItemId) {
      getItemDetail(resolvedItemId);
    }
  }, [itemIdProp, router?.location?.state?.itemId]);

  /* A function that is used to update the basic info in the state. */
  const handleBasicInfo = (key, val) => {
    setItem((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        baseLanguage: {
          ...prevState.values.baseLanguage,
          [key]: val,
        },
      },
      errors: {
        ...prevState.errors,
        baseLanguage: {
          ...prevState.errors.baseLanguage,
          [key]: "",
        },
      },
    }));
    setIsDirty(true);
  };

  /* A function that is used to update the basic info in the state. */
  const handleValues = (key, val) => {
    setItem((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        [key]: val,
      },
      errors: {
        ...prevState.errors,
        [key]: "",
      },
    }));
    setIsDirty(true);
  };

  /* Validating the inputs. */
  const validateInputs = () => {
    let { objError, isValidForm } = UTILS.validateItemInfo(item);
    setItem((prevState) => ({
      values: {
        ...prevState.values,
      },
      errors: objError,
    }));
    return isValidForm;
  };

  /**
   * An async function that updates an item.
   */

  const updateItem = async () => {
    if (!validateInputs()) return;
    try {
      setIsDirty(false);
      setLoading(true);
      const payload = transformPayload(item?.values);
      const result = await API.UPDATE_ITEM(payload);

      if (result?.success === 1) {
        if (toBeAttachedCustomizations?.length > 0) {
          await linkModifierGroupsWithItems({
            itemId: result?.data?._id,
            attachedGroups: toBeAttachedCustomizations,
          });
        }
        if (toBeUnLinkCustomizations?.length > 0) {
          for (const customization of toBeUnLinkCustomizations) {
            await UnlinkModiferGroupFromItem({
              itemId: result?.data?._id,
              modifierGroupId: customization?._id,
            });
          }
        }
        UTILS.successNotification(t("Item Updated"));
        if (typeof onUpdated === "function") {
          onUpdated(result?.data);
        } else {
          router.goBack();
        }
      } else {
        UTILS.errorNotification(errorHandler(result?.message));
      }
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.error?.message));
      setIsDirty(true);
    } finally {
      setLoading(false);
    }
  };

  const handleStockEnabledValues = (e) => {
    setItem((prevState) => {
      const { availableStock, lowStockThreshold } = prevState.values;

      return {
        ...prevState,
        values: {
          ...prevState.values,
          isStockEnabled: e.target.checked,
          availableStock: availableStock ?? 0, // if undefined, set to 0
          lowStockThreshold: lowStockThreshold ?? 0, // if undefined, set to 0
        },
        errors: {
          ...prevState.errors,
          isStockEnabled: "",
        },
      };
    });
    setIsDirty(true);
  };

  return (
    <>
      <UnsavedChangesWarning hasUnsavedChanges={isDirty && JSON.stringify(originalItem?.values) !== JSON.stringify(item?.values)} />

      <AppContainer
        title={t("Update Item")}
        loading={loading}
        subTitle={t("Please enter complete details for better user experience")}
        containerProps={{
          id: "appContainerItemForm",
        }}
      >
        <ItemsForm
          id="updateItemForm"
          item={item}
          handleBasicInfo={handleBasicInfo}
          handleValues={handleValues}
          setItem={setItem}
          onSave={updateItem}
          setIsDirty={setIsDirty}
          setLoading={setLoading}
          itemId={itemId}
          handleStockEnabledValues={handleStockEnabledValues}
          content={
            <AttachItemToModifier
              toBeAttachedCustomizations={toBeAttachedCustomizations}
              setToBeAttachedCustomizations={setToBeAttachedCustomizations}
              toBeUnLinkCustomizations={toBeUnLinkCustomizations}
              setToBeUnLinkCustomizations={setToBeUnLinkCustomizations}
              itemDetail={item?.values}
              getItemDetail={getItemDetail}
              itemId={itemId}
              />
          }
          onBack={inline ? onCancel : null}
        />
      </AppContainer>
    </>
  );
};

export default UpdateItem;
